import { Switch } from '@affine/component';
import { cssVar } from '@streamlain/theme';
import type { CSSProperties } from 'react';

export type ToggleState = 'off' | 'on' | 'mixed';

export function getToggleState<T>(
  items: T[],
  isEnabled: (item: T) => boolean
): ToggleState {
  if (items.length === 0) return 'off';
  const enabledCount = items.filter(isEnabled).length;
  if (enabledCount === 0) return 'off';
  if (enabledCount === items.length) return 'on';
  return 'mixed';
}

export const MixedSwitch = ({
  state,
  disabled,
  onToggle,
  testId,
}: {
  state: ToggleState;
  disabled?: boolean;
  onToggle: () => void;
  testId?: string;
}) => {
  const checked = state === 'on';
  const wrapperStyle: CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    border: 0,
    background: 'transparent',
    cursor: disabled ? 'not-allowed' : 'pointer',
  };

  if (state !== 'mixed') {
    return (
      <Switch
        data-testid={testId}
        checked={checked}
        disabled={disabled}
        onClick={event => event.stopPropagation()}
        onChange={() => {
          if (!disabled) onToggle();
        }}
      />
    );
  }

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked="mixed"
      data-testid={testId}
      disabled={disabled}
      style={{
        ...wrapperStyle,
        width: 46,
        height: 26,
        borderRadius: 37,
        backgroundColor: cssVar('toggleDisableBackgroundColor'),
        boxShadow: `0 0 0 1px ${cssVar('primaryColor')}`,
        opacity: disabled ? 0.5 : 1,
      }}
      onClick={event => {
        event.stopPropagation();
        if (!disabled) onToggle();
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: 20,
          height: 20,
          borderRadius: 999,
          backgroundColor: cssVar('toggleCircleBackgroundColor'),
          transform: 'translate(-50%, -50%)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          pointerEvents: 'none',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: 10,
            height: 2,
            borderRadius: 999,
            backgroundColor: cssVar('primaryColor'),
            transform: 'translate(-50%, -50%)',
          }}
        />
      </span>
    </button>
  );
};
